import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import TimelineConfiguratorTabBase from "./TimelineConfiguratorTabBase.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { DateHelper, MessageDialog, StringHelper } from "@bryntum/schedulerpro";
import { GRANTS } from "../../../other/constants/PrivilegeTypes.jsx";
import { cellMenuItemRemoveRow } from "../../../other/utils/gridCellMenuUtils.jsx";
import { PLUGINS_KEYS } from "../../../other/constants/PluginKeys.jsx";
import { TIMELINE_CONG_KEYS } from "../../../other/constants/OtherKeys.jsx";
import { getScheduleInstanceFromViewManager } from "../../../other/utils/otherUtils.jsx";
import { defaultAddDeleteButton } from "../../../other/utils/utils.jsx";

const weekDays = DateHelper.getDayNames();

export default class TimelineConfiguratorNonWorkingDaysTab extends TimelineConfiguratorTabBase {
  static get $name() {
    return "TimelineConfiguratorNonWorkingDaysTab";
  }

  static get type() {
    return "timelineconfiguratornonworkingdaystab";
  }

  get stateId() {
    return PERSISTENCE_STATE_KEYS.timelineConfiguratorEditorNonWorkingDaysGridState;
  }

  static get configurable() {
    return {
      title: "Non Working Time",
      items: {
        nonWorkingTimeGrid: {
          height: "100%",
          type: WIDGET_KEYS.swivuGrid,
          selectionMode: {
            multiSelect: false,
          },
          features: {
            headerMenu: false,
            sort: false,
            group: false,
            cellMenu: {
              items: {
                cut: false,
                copy: false,
                paste: false,
                removeRow: cellMenuItemRemoveRow({
                  onItem: "up.onRemoveNonWorking"
                }),
              }
            },
          },
          listeners: {
            finishCellEdit: "up.onFinishCellEdit"
          },
          columns: [
            {
              type: "check",
              field: "isEnableNonWorkingTime",
              listeners: {
                toggle: "up.onFinishCellEdit",
              }
            },
            {
              id: "dayOfTheWeek",
              text: "Day of the Week",
              width: "12em",
              field: "dayName",
              editor: {
                type: "combo",
                items: weekDays,
                flex: 1,
              }
            },
            {
              id: "allDay",
              text: "All Day",
              type: "check",
              field: "allDay",
              width: "7em",
              listeners: {
                beforeToggle: "up.onAllDayChange"
              }
            },
            {
              id: "begin",
              text: "Begin",
              field: "beginTime",
              type: "time",
              width: "12em",
              finalizeCellEdit: ({ record, value }) => {
                const beginTime = DateHelper.format(value, "HH:mm");
                return record.end ? record.end > beginTime : true;
              }
            },
            {
              id: "end",
              text: "End",
              field: "endTime",
              type: "time",
              width: "12em",
              finalizeCellEdit: ({ record, value }) => {
                const endTime = DateHelper.format(value, "HH:mm");
                return record.begin ? record.begin < endTime : true;
              }
            }
          ],
          bbar: {
            items: {
              ...defaultAddDeleteButton({
                addButton: {
                  onClick: "up.onAddNonWorking",
                  cls: 'bg-[transparent] text-[#228a0c] border-[1.5px] !rounded-md dark:border-[#373638]',
                  text: "Add Days",
                  icon: "b-fa b-fa-plus b-button-icon",
                },
                deleteButton: {
                  onClick: "up.onRemoveNonWorking",
                  text: "Delete",
                  cls: '!bg-[transparent] [&:not(.b-disabled):!text-[#f6971e] border-[1.5px] !rounded-md !border-[#dfdfdf] dark:!border-[#373638]',
                  icon: "fa-light fa-trash-check",
                },
              }),
              doneButton: {
                type: "button",
                icon: "fa-light fa-close",
                text: "Done",
                cls: 'bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md',
                onClick: "up.close",
              },
            }
          }
        },
      }
    }
  }

  loadRecord(record, context) {
    super.loadRecord(record, context);

    const { nonWorkingTimeGrid} = this.widgetMap;

    const nonWorkingTimeChecks = getScheduleInstanceFromViewManager(this.context.viewManager).features[PLUGINS_KEYS.swivuTimelineConfigurator].state[TIMELINE_CONG_KEYS.nonWorkingTimeChecks];

    nonWorkingTimeGrid.store = context.project.nonWorkingTimeStore;

    if (nonWorkingTimeChecks) {
      nonWorkingTimeGrid.store.forEach((record) => {
        record.isEnableNonWorkingTime = nonWorkingTimeChecks.includes(record.id)
      })
    }
  }

  onFinishCellEdit() {
    this.persistSettings();
  }

  persistSettings() {
    const { nonWorkingTimeGrid} = this.widgetMap;

    const nonWorkingSelectedItems = nonWorkingTimeGrid.store.query((r) => r.isEnableNonWorkingTime).map((r) => r.id);
    getScheduleInstanceFromViewManager(this.context.viewManager).features[PLUGINS_KEYS.swivuTimelineConfigurator].swivuExecuteSettings(TIMELINE_CONG_KEYS.nonWorkingTimeChecks, nonWorkingSelectedItems);
  }

  onAddNonWorking() {
    const { nonWorkingTimeGrid} = this.widgetMap;
    const id = StringHelper.generateUUID();

    nonWorkingTimeGrid.store.add({
      id,
      dayId: 0,
      allDay: true,
      // we need time only not day... so just ignore the day
      beginDate: null,
      endDate: null
    });
  }

  onRemoveNonWorking() {
    const { nonWorkingTimeGrid} = this.widgetMap;
    const selectedRecord = nonWorkingTimeGrid.selectedRecord;
    if (selectedRecord.isDefault) {
      MessageDialog.alert({
        title: "Default Non Working Days",
        message: "Cannot delete the default non working time records."
      })
    } else {
      nonWorkingTimeGrid.store.remove(selectedRecord);
      this.persistSettings();
    }
  }

  isEnableNonWorkingTimeChange() {
    this.persistSettings();
  }

  onAllDayChange({ record, checked, source }) {
    if (!record.dayName) return false;

    // Manually Assigning updated value to record because before change listener is used
    record[source.field] = checked;

    this.persistSettings();

    return true;
  }

  swivuCheckGrants(user) {
    if (!user.can(GRANTS.grant_one)) {
      const { nonWorkingTimeGrid } = this.widgetMap;
      nonWorkingTimeGrid.disabled = true;
      nonWorkingTimeGrid.readOnly = true;
      nonWorkingTimeGrid.bbar.items.forEach(
        (item) => (item.disabled = true)
      );
    }
  }
}

TimelineConfiguratorNonWorkingDaysTab.initClass();
